"use client";

import { createContext, useContext, type ReactNode } from "react";

/**
 * Configuração pública, resolvida em runtime pelo Server Component raiz
 * (`src/app/layout.tsx`) e entregue aos Client Components via Contexto — nunca via
 * `process.env.NEXT_PUBLIC_*` (que ficaria embutido no JavaScript no momento do
 * `next build`) e nunca via uma rota de API deste repositório, que não expõe rota
 * alguma (ver docs/adr/0013-sem-bff-neste-repositorio.md).
 *
 * Isso é o que permite que a MESMA imagem Docker sirva DEV, HML e PRD — inclusive
 * `supabaseUrl`/`supabasePublishableKey`, que apontam para um projeto Supabase
 * diferente em cada ambiente (ver docs/adr/0014-autenticacao-supabase-direto.md). O
 * valor é calculado a cada requisição, a partir das variáveis de ambiente reais do
 * container em execução. Ver docs/adr/0012-config-runtime-por-ambiente.md.
 */
export interface PublicEnv {
  environment: "development" | "staging" | "production";
  /** URL pública da API do backend, chamada diretamente pelo navegador. */
  apiPublicBaseUrl: string;
  /** Projeto Supabase deste ambiente — usado pelo cliente Supabase do navegador. */
  supabaseUrl: string;
  /** Publishable key (equivalente à antiga anon key): segura para o navegador. */
  supabasePublishableKey: string;
  sentryDsnPublico: string;
}

const PublicEnvContext = createContext<PublicEnv | undefined>(undefined);

export function PublicEnvProvider({
  value,
  children,
}: {
  value: PublicEnv;
  children: ReactNode;
}) {
  return (
    <PublicEnvContext.Provider value={value}>
      {children}
    </PublicEnvContext.Provider>
  );
}

/** Só pode ser usado dentro de `<PublicEnvProvider>` (ver src/app/layout.tsx). */
export function usePublicEnv(): PublicEnv {
  const value = useContext(PublicEnvContext);
  if (!value) {
    throw new Error(
      "usePublicEnv() foi chamado fora de <PublicEnvProvider>. Confira src/app/layout.tsx.",
    );
  }
  return value;
}
